// SSE (Server-Sent Events) debugging and error handling utilities
import { logger } from './logger';

export interface SSEErrorInfo {
  type: 'connection' | 'authentication' | 'network' | 'parse' | 'server' | 'unknown';
  message: string;
  details?: any;
  timestamp: string;
  url?: string;
  readyState?: number;
}

export interface SSEDebugInfo {
  url: string;
  storeId?: number;
  tableId?: number;
  authHeaders: Record<string, string>;
  readyState: number;
  lastError?: SSEErrorInfo;
  connectionTime?: string;
  messageCount: number;
}

class SSELogger {
  private errors: SSEErrorInfo[] = [];
  private debugInfo: SSEDebugInfo | null = null;
  private maxErrors = 20;

  logError(type: SSEErrorInfo['type'], message: string, details?: any, url?: string, readyState?: number) {
    const error: SSEErrorInfo = {
      type,
      message,
      details,
      timestamp: new Date().toISOString(),
      url,
      readyState
    };

    this.errors.push(error);
    
    if (this.errors.length > this.maxErrors) {
      this.errors.shift();
    }

    logger.error(`[SSE ${type.toUpperCase()}]`, message, details);
  }

  setDebugInfo(info: SSEDebugInfo) {
    this.debugInfo = info;
  }

  getErrors(): SSEErrorInfo[] {
    return [...this.errors];
  }

  getDebugInfo(): SSEDebugInfo | null {
    return this.debugInfo;
  }

  clearErrors(): void {
    this.errors = [];
  }

  getErrorCount(): number {
    return this.errors.length;
  }
}

export const sseLogger = new SSELogger();

// SSE connection status checker
export const checkSSEConnection = async (url: string, headers: Record<string, string>): Promise<{
  canConnect: boolean;
  error?: string;
  status?: number;
}> => {
  try {
    // Try a regular fetch first to check if the endpoint is reachable
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...headers,
        'Accept': 'text/event-stream',
        'Cache-Control': 'no-cache'
      }
    });

    return {
      canConnect: response.ok,
      status: response.status,
      error: response.ok ? undefined : `HTTP ${response.status}: ${response.statusText}`
    };
  } catch (error) {
    return {
      canConnect: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

// Enhanced SSE error categorization
export const categorizeSSEError = (readyState: number, error: any): SSEErrorInfo['type'] => {
  if (readyState === 0) { // CONNECTING
    return 'connection';
  } else if (readyState === 2) { // CLOSED
    if (error?.status === 401 || error?.status === 403) {
      return 'authentication';
    } else if (error?.status >= 500) {
      return 'server';
    } else if (error?.status >= 400) {
      return 'network';
    }
  }
  return 'unknown';
};

// SSE URL validation
export const validateSSEUrl = (url: string, storeId?: number, tableId?: number): {
  isValid: boolean;
  errors: string[];
} => {
  const errors: string[] = [];

  try {
    const urlObj = new URL(url);
    
    if (!urlObj.protocol.startsWith('http')) {
      errors.push('URL must use HTTP/HTTPS protocol');
    }

    if (!urlObj.pathname.includes('/sse/')) {
      errors.push('URL must contain /sse/ path');
    }

    const params = urlObj.searchParams;
    if (!params.has('storeId')) {
      errors.push('Missing storeId parameter');
    } else if (storeId && params.get('storeId') !== storeId.toString()) {
      errors.push('storeId parameter mismatch');
    }

    if (!params.has('tableId')) {
      errors.push('Missing tableId parameter');
    } else if (tableId && params.get('tableId') !== tableId.toString()) {
      errors.push('tableId parameter mismatch');
    }

  } catch (error) {
    errors.push('Invalid URL format');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Authentication header validation
export const validateAuthHeaders = (headers: Record<string, string>): {
  isValid: boolean;
  errors: string[];
} => {
  const errors: string[] = [];

  if (!headers.Authorization) {
    errors.push('Missing Authorization header');
  } else if (!headers.Authorization.startsWith('Bearer ')) {
    errors.push('Authorization header must start with "Bearer "');
  } else if (headers.Authorization === 'Bearer ') {
    errors.push('Authorization token is empty');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Comprehensive SSE debugging
export const debugSSEConnection = async (
  url: string, 
  headers: Record<string, string>, 
  storeId?: number, 
  tableId?: number
): Promise<{
  urlValidation: { isValid: boolean; errors: string[] };
  authValidation: { isValid: boolean; errors: string[] };
  connectionTest: { canConnect: boolean; error?: string; status?: number };
  recommendations: string[];
}> => {
  const urlValidation = validateSSEUrl(url, storeId, tableId);
  const authValidation = validateAuthHeaders(headers);
  const connectionTest = await checkSSEConnection(url, headers);

  const recommendations: string[] = [];

  if (!urlValidation.isValid) {
    recommendations.push('Fix URL validation errors');
  }

  if (!authValidation.isValid) {
    recommendations.push('Check authentication token in localStorage');
  }

  if (!connectionTest.canConnect) {
    if (connectionTest.status === 401) {
      recommendations.push('Authentication failed - check token validity');
    } else if (connectionTest.status === 403) {
      recommendations.push('Access forbidden - check store/table permissions');
    } else if (connectionTest.status === 404) {
      recommendations.push('SSE endpoint not found - check server configuration');
    } else if (connectionTest.status && connectionTest.status >= 500) {
      recommendations.push('Server error - check backend logs');
    } else {
      recommendations.push('Network connectivity issue - check internet connection');
    }
  }

  return {
    urlValidation,
    authValidation,
    connectionTest,
    recommendations
  };
};

// Log comprehensive SSE debug info
export const logSSEDebugInfo = async (
  url: string, 
  headers: Record<string, string>, 
  storeId?: number, 
  tableId?: number
): Promise<void> => {
  logger.group('🔍 SSE Connection Debug Info');
  
  const debugResult = await debugSSEConnection(url, headers, storeId, tableId);
  
  logger.log('📋 URL:', url);
  // logger.log('🔑 Headers:', headers);
  logger.log('🏪 Store ID:', storeId);
  logger.log('🪑 Table ID:', tableId);
  
  logger.group('✅ Validation Results');
  logger.log('URL Validation:', debugResult.urlValidation);
  logger.log('Auth Validation:', debugResult.authValidation);
  logger.log('Connection Test:', debugResult.connectionTest);
  logger.groupEnd();
  
  if (debugResult.recommendations.length > 0) {
    logger.group('💡 Recommendations');
    debugResult.recommendations.forEach((rec, index) => {
      logger.log(`${index + 1}. ${rec}`);
    });
    logger.groupEnd();
  }
  
  logger.log('📊 SSE Logger Errors:', sseLogger.getErrors());
  logger.log('📊 SSE Logger Error Count:', sseLogger.getErrorCount());
  
  logger.groupEnd();
}; 